import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getActiveFestival, getFestivalById, isLoginGreetingPending, clearLoginGreeting, setGreetingOpen } from '../../utils/festivals';
import { runEffect } from './festivalEffects';
import { useAuth } from '../../context/AuthContext';

const AUTO_DISMISS_MS = 6000;
const FADE_MS = 500;

// Full-screen festival animation + greeting banner.
// Drop <FestivalGreeting /> into a dashboard. It shows right after every
// successful login while a festival is active (see requestLoginGreeting in
// AuthContext), but not on page refreshes or normal navigation.
//
// Preview any festival on any day by adding ?festival=<id> to the URL,
// e.g. /admin?festival=holi  (ids: diwali, holi, makar-sankranti, christmas, new-year).
export default function FestivalGreeting() {
  const { user } = useAuth();
  const userName = user?.name?.split(' ')[0];
  const [festival, setFestival] = useState(null);
  const [phase, setPhase] = useState('hidden'); // hidden → entering → shown → leaving
  const canvasRef = useRef(null);

  // Decide once, on mount, whether to show anything.
  useEffect(() => {
    const previewId = new URLSearchParams(window.location.search).get('festival');
    if (previewId) {
      const f = getFestivalById(previewId);
      if (f) setFestival(f);
      return;
    }
    if (!isLoginGreetingPending()) return; // not straight after a login (e.g. a refresh)
    const active = getActiveFestival();
    if (active) setFestival(active);
    else clearLoginGreeting();
  }, []);

  useEffect(() => {
    if (!festival) return;
    setPhase('entering');
    setGreetingOpen(true); // ambient theme waits until the greeting closes
    const id = setTimeout(() => setPhase('shown'), 30); // let the hidden state paint first
    return () => { clearTimeout(id); setGreetingOpen(false); };
  }, [festival]);

  const dismiss = useCallback(() => {
    clearLoginGreeting(); // don't replay if the user comes back to the dashboard
    setGreetingOpen(false); // ambient theme fades in while this fades out
    setPhase((p) => (p === 'hidden' || p === 'leaving' ? p : 'leaving'));
  }, []);

  // Auto-dismiss, Escape key, and final unmount after the fade-out.
  useEffect(() => {
    if (phase === 'shown') {
      const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
      const onKey = (e) => e.key === 'Escape' && dismiss();
      window.addEventListener('keydown', onKey);
      return () => { clearTimeout(timer); window.removeEventListener('keydown', onKey); };
    }
    if (phase === 'leaving') {
      const timer = setTimeout(() => { setPhase('hidden'); setFestival(null); }, FADE_MS);
      return () => clearTimeout(timer);
    }
  }, [phase, dismiss]);

  // Start the canvas animation (skipped for users who prefer reduced motion).
  useEffect(() => {
    if (!festival || !canvasRef.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    return runEffect(canvasRef.current, festival.effect, { palette: festival.palette });
  }, [festival]);

  if (!festival) return null;

  const visible = phase === 'shown';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="festival-greeting-title"
      onClick={dismiss}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/60"
      style={{ opacity: visible ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-white/95 shadow-2xl overflow-hidden text-center"
        style={{
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(12px)',
          transition: `transform ${FADE_MS}ms cubic-bezier(.2,.9,.3,1.2)`,
          borderTop: `4px solid ${festival.accent}`,
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Skip greeting"
          className="absolute top-3 right-3 flex items-center gap-1 pl-2.5 pr-2 py-1 rounded-full text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
        >
          Skip <X size={16} />
        </button>

        <div className="px-6 pt-8 pb-6">
          <div className="text-5xl mb-3" aria-hidden="true">{festival.emoji}</div>
          <h2 id="festival-greeting-title" className="text-2xl font-bold" style={{ color: festival.accent }}>
            {festival.title}
          </h2>
          {userName && <p className="mt-1 text-sm font-medium text-gray-500">Dear {userName},</p>}
          <p className="mt-3 text-gray-600 leading-relaxed">{festival.message}</p>
          <button
            type="button"
            onClick={dismiss}
            className="mt-5 px-5 py-2 rounded-lg text-sm font-semibold text-white shadow-sm hover:opacity-90"
            style={{ backgroundColor: festival.accent }}
          >
            Continue to dashboard
          </button>
        </div>

        {/* countdown bar for the auto-dismiss */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full"
            style={{
              backgroundColor: festival.accent,
              width: visible ? '0%' : '100%',
              transition: visible ? `width ${AUTO_DISMISS_MS}ms linear` : 'none',
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
